import { useState, useEffect } from 'react';
import { createApiClient } from '../../../services/ApiClient.jsx';

export const useSubmission = (accessToken, formId) => {
    const [submissionId, setSubmissionId] = useState(null);
    const [answers, setAnswers] = useState({});
    const [feedback, setFeedback] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [gamificationRewards, setGamificationRewards] = useState(null);
    const [error, setError] = useState(null);

    const apiClient = createApiClient(accessToken);

    // localStorage key for this form
    const storageKey = `submission_${formId}`;

    // Load from localStorage on mount
    useEffect(() => {
        if (formId) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    const data = JSON.parse(stored);
                    setAnswers(data.answers || {});
                    setSubmissionId(data.submissionId);
                    console.log('Loaded from localStorage:', data);
                } catch (err) {
                    console.error('Error loading from localStorage:', err);
                    localStorage.removeItem(storageKey);
                }
            }
        }
    }, [formId]);

    // Save to localStorage whenever answers change
    useEffect(() => {
        if (formId && submissionId) {
            const dataToStore = {
                submissionId,
                answers,
                timestamp: Date.now()
            };
            localStorage.setItem(storageKey, JSON.stringify(dataToStore));
        }
    }, [answers, submissionId, formId]);

    const startSubmission = async () => {
        try {
            if (!formId) {
                throw new Error('Form ID not available');
            }

            console.log('Starting NEW submission for form:', formId);

            const response = await apiClient.post('/api/submissions/start', {
                form_id: formId
            });
            
            setSubmissionId(response.submission_id);
            console.log('Started NEW submission:', response.submission_id);
            
            // Clear old localStorage data for fresh start
            localStorage.removeItem(storageKey);
            
            // Reset states for new submission
            setAnswers({});
            setFeedback({});
            setIsSubmitted(false);
            setSubmitSuccess(false);
            setGamificationRewards(null);
            setError(null);
            
        } catch (err) {
            console.error('Error starting submission:', err);
            setError('Failed to start submission. Please try again.');
        }
    };

    const submitSection = async (sectionQuestions) => {
        if (!submissionId) {
            console.error('No submission ID available');
            return;
        }

        if (submitting) {
            console.log('Already submitting section, skipping...');
            return;
        }

        if (isSubmitted) {
            console.log('Submission already completed, skipping section submit...');
            return;
        }

        // Additional guard using a ref to prevent rapid fire calls
        if (submitSection.isRunning) {
            console.log('submitSection already running, skipping...');
            return;
        }

        console.log('Submitting section with submission ID:', submissionId);

        try {
            submitSection.isRunning = true;
            setSubmitting(true);
            
            // All answers should already be saved, but let's make sure by sending them again
            const sectionAnswers = sectionQuestions.map(question => ({
                question_id: question.id,
                value: answers[question.id] || ''
            }));

            console.log('Section answers (final submit):', sectionAnswers);

            // Submit answers and get feedback
            const response = await apiClient.post(`/api/submissions/${submissionId}/answers`, {
                answers: sectionAnswers
            });

            console.log('Submit response:', response);

            // Process feedback
            const newFeedback = { ...feedback };
            response.feedback.forEach(fb => {
                newFeedback[fb.question_id] = fb;
            });
            setFeedback(newFeedback);

            return response.feedback;

        } catch (err) {
            console.error('Error submitting section:', err);
            setError('Failed to submit section. Please try again.');
            throw err;
        } finally {
            setSubmitting(false);
            submitSection.isRunning = false;
        }
    };

    const completeSubmission = async () => {
        if (!submissionId || isSubmitted) {
            console.log('Submission already completed or no submission ID');
            return;
        }

        // Additional guard using a ref to prevent rapid fire calls
        if (completeSubmission.isRunning) {
            console.log('completeSubmission already running, skipping...');
            return;
        }
        
        try {
            completeSubmission.isRunning = true;
            console.log('Completing submission:', submissionId);
            
            const response = await apiClient.post(`/api/submissions/${submissionId}/complete`);
            
            setIsSubmitted(true);
            setSubmitSuccess(true);
            
            // Store gamification rewards if present (backend sends xp_notification)
            if (response.xp_notification) {
                setGamificationRewards(response.xp_notification);
            }
            
            // Clear localStorage after successful completion
            localStorage.removeItem(storageKey);
            
            console.log('Submission completed successfully');
            console.log('XP notification:', response.xp_notification);
        } catch (err) {
            console.error('Error completing submission:', err);
            setError('Failed to complete submission. Please try again.');
        } finally {
            completeSubmission.isRunning = false;
        }
    };

    const restartSubmission = async () => {
        try {
            setSubmitting(true);
            
            console.log('RESTARTING - creating NEW submission');
            
            // Start completely new submission (not restart existing one)
            await startSubmission();
            
        } catch (err) {
            console.error('Error restarting submission:', err);
            setError('Failed to restart submission. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const updateAnswer = (questionId, answer) => {
        if (!isSubmitted) {
            console.log(`Updating answer for question ${questionId}:`, answer);
            setAnswers(prev => ({
                ...prev,
                [questionId]: answer
            }));
            // localStorage wird automatisch über useEffect gespeichert
        }
    };

    return {
        submissionId,
        answers,
        feedback,
        isSubmitted,
        submitSuccess,
        submitting,
        error,
        gamificationRewards,
        startSubmission,
        submitSection,
        completeSubmission,
        restartSubmission,
        updateAnswer,
        setError
    };
};
